import { ContainerModule } from 'inversify';
import { getMongoClient, DI_MONGO_CLIENT, getMongoConfig } from "@swoft/persistence";
import { IEventBus } from '@swoft/core';
import { InMemoryEventBus } from '../../../../infrastructure/events/InMemoryEventBus';
import { MongoDBAdapter } from '../../../../infrastructure/adapters/MongoDBAdapter';
import { MongoDomainRepository } from '../repositories/MongoDomainRepository';
import { MongoBoundedContextRepository } from '../repositories/MongoBoundedContextRepository';
import { MongoAggregateRepository } from '../repositories/MongoAggregateRepository';

// Application Services
import { DomainApplicationService } from '../../../../application/services/DomainApplicationService';
import { BoundedContextApplicationService } from '../../../../application/services/BoundedContextApplicationService';
import { AggregateApplicationService } from '../../../../application/services/AggregateApplicationService';
import { ProjectApplicationService } from '../../../../application/services/ProjectApplicationService';
import { PackageMappingApplicationService } from '../../application/PackageMappingApplicationService';

// Query Services
import { DomainCQRSQueryService } from '../../../strategic-design/application/services/DomainQueryService';
import { BoundedContextQueryService } from '../../../strategic-design/application/services/BoundedContextQueryService';
import { AggregateQueryService } from '../../../tactical-design/application/services/AggregateQueryService';
import { ProjectQueryService } from '../../../strategic-design/application/services/ProjectQueryService';

// Command Services
import { DomainCommandService } from '../../../strategic-design/application/services/DomainCommandService';
import { BoundedContextCommandService } from '../../../strategic-design/application/services/BoundedContextCommandService';
import { AggregateCommandService } from '../../../tactical-design/application/services/AggregateCommandService';
import { ProjectCommandService } from '../../../strategic-design/application/services/ProjectCommandService';

// Repository Implementations are dynamically imported in factory functions

// Repository Ports
import type { IDomainRepository } from '../../domain/ports/IDomainRepository';
import type { IBoundedContextRepository } from '../../domain/ports/IBoundedContextRepository';
import type { IAggregateRepository } from '../../domain/ports/IAggregateRepository';
import type { IProjectRepository } from '../../domain/ports/IProjectRepository';
import type { IPackageMappingRepository } from '../../domain/ports/types';
import { IPackageMappingRepositorySymbol } from '../../domain/ports/symbols';

// Command Handlers
import { CreateDomainHandler } from '../../../strategic-design/application/handlers/CreateDomainHandler';
import { UpdateDomainHandler } from '../../../strategic-design/application/handlers/UpdateDomainHandler';
import { GetDomainHandler } from '../../../strategic-design/application/handlers/GetDomainHandler';
import { ListDomainsHandler } from '../../../strategic-design/application/handlers/ListDomainsHandler';
import { CreateBoundedContextHandler } from '../../../strategic-design/application/handlers/CreateBoundedContextHandler';
import { GetBoundedContextHandler } from '../../../strategic-design/application/handlers/GetBoundedContextHandler';
import { ListBoundedContextsHandler } from '../../../strategic-design/application/handlers/ListBoundedContextsHandler';
import { CreateAggregateHandler } from '../../../tactical-design/application/handlers/CreateAggregateHandler';
import { GetAggregateHandler } from '../../../tactical-design/application/handlers/GetAggregateHandler';
import { CreateProjectHandler } from '../../../strategic-design/application/handlers/CreateProjectHandler';
import { GetProjectHandler } from '../../../strategic-design/application/handlers/GetProjectHandler';
import { ListProjectsHandler } from '../../../strategic-design/application/handlers/ListProjectsHandler';

// Meta-Model Design Bounded Context
import { META_MODEL_DESIGN_SYMBOLS } from '../../../meta-model-design/infrastructure/di/MetaModelDesignContainerModule';

// Import symbols from centralized location to avoid circular dependencies
import { DDD_SYMBOLS } from '../../../../infrastructure/di/DDD_SYMBOLS';
export { DDD_SYMBOLS };

// Symbols are now imported from central location above

/**
 * Create DDD Module - Following the same pattern as party-manager
 * 
 * This function returns a ContainerModule that configures all DDD dependencies.
 * Use this instead of DDDModule.configure() for consistency with other packages.
 * 
 * Usage:
 * ```typescript
 * import { createDDDModule } from "@swoft/core";
 * 
 * const container = new Container();
 * const dddModule = createDDDModule();
 * container.load(dddModule);
 * ```
 */
export const createDDDModule = (): ContainerModule => {
  return new ContainerModule((bind) => {
    // Get database configuration
    const cfg = getMongoConfig();
    const databaseName = cfg?.dbName;

    // =====================================
    // DATABASE PORT (Infrastructure Layer)
    // =====================================
    
    // Database Port - Following Port/Adapter pattern from docs
    bind<import('../../../../domain/ports/DatabasePort').DatabasePort>(DDD_SYMBOLS.DatabasePort)
      .to(MongoDBAdapter)
      .inSingletonScope();

    // =====================================
    // REPOSITORIES (Infrastructure Layer)
    // =====================================
    
    // Domain Repository
    bind<IDomainRepository>(DDD_SYMBOLS.IDomainRepository)
      .to(MongoDomainRepository)
      .inSingletonScope();

    // Bounded Context Repository
    bind<IBoundedContextRepository>(DDD_SYMBOLS.IBoundedContextRepository)
      .to(MongoBoundedContextRepository)
      .inSingletonScope();

    // Aggregate Repository
    bind<IAggregateRepository>(DDD_SYMBOLS.IAggregateRepository)
      .to(MongoAggregateRepository)
      .inSingletonScope();

    // Project Repository (placeholder using domain repository)
    bind<IProjectRepository>(DDD_SYMBOLS.IProjectRepository)
      .toDynamicValue(() => {
        const { MongoDomainRepository } = require('../repositories/MongoDomainRepository');
        return new MongoDomainRepository() as unknown as IProjectRepository;
      })
      .inSingletonScope();

    // Package Mapping Repository - Use Symbol for injection
    bind<IPackageMappingRepository>(IPackageMappingRepositorySymbol)
      .toDynamicValue((context) => {
        const mongoClient = context.container.get<import("mongodb").MongoClient>(DI_MONGO_CLIENT);
        const { MongoPackageMappingRepository } = require('../repositories/MongoPackageMappingRepository');
        return new MongoPackageMappingRepository(mongoClient, databaseName);
      })
      .inSingletonScope();

    // =====================================
    // QUERY SERVICES (Application Layer)
    // =====================================
    
    // Domain Query Service
    bind<DomainCQRSQueryService>(DDD_SYMBOLS.DomainCQRSQueryService)
      .toDynamicValue((context) => {
        const domainRepo = context.container.get<IDomainRepository>(DDD_SYMBOLS.IDomainRepository);
        const boundedContextRepo = context.container.get<IBoundedContextRepository>(DDD_SYMBOLS.IBoundedContextRepository);
        return new DomainCQRSQueryService(domainRepo, boundedContextRepo);
      })
      .inSingletonScope();

    // Bounded Context Query Service
    bind<BoundedContextQueryService>(DDD_SYMBOLS.BoundedContextQueryService)
      .toDynamicValue((context) => {
        const boundedContextRepo = context.container.get<IBoundedContextRepository>(DDD_SYMBOLS.IBoundedContextRepository);
        const domainRepo = context.container.get<IDomainRepository>(DDD_SYMBOLS.IDomainRepository);
        return new BoundedContextQueryService(boundedContextRepo, domainRepo);
      })
      .inSingletonScope();

    // Aggregate Query Service
    bind<AggregateQueryService>(DDD_SYMBOLS.AggregateQueryService)
      .toDynamicValue((context) => {
        const aggregateRepo = context.container.get<IAggregateRepository>(DDD_SYMBOLS.IAggregateRepository);
        const boundedContextRepo = context.container.get<IBoundedContextRepository>(DDD_SYMBOLS.IBoundedContextRepository);
        return new AggregateQueryService(aggregateRepo, boundedContextRepo);
      })
      .inSingletonScope();

    // Project Query Service
    bind<ProjectQueryService>(DDD_SYMBOLS.ProjectQueryService)
      .to(ProjectQueryService)
      .inSingletonScope();

    // =====================================
    // COMMAND SERVICES (Application Layer)
    // =====================================
    
    bind<DomainCommandService>(DDD_SYMBOLS.DomainCommandService)
      .to(DomainCommandService)
      .inSingletonScope();

    bind<BoundedContextCommandService>(DDD_SYMBOLS.BoundedContextCommandService)
      .to(BoundedContextCommandService)
      .inSingletonScope();

    bind<AggregateCommandService>(DDD_SYMBOLS.AggregateCommandService)
      .to(AggregateCommandService)
      .inSingletonScope();

    bind<ProjectCommandService>(DDD_SYMBOLS.ProjectCommandService)
      .to(ProjectCommandService)
      .inSingletonScope();

    // =====================================
    // EVENT BUS (Infrastructure Layer)
    // =====================================
    
    bind<IEventBus>(DDD_SYMBOLS.IEventBus)
      .to(InMemoryEventBus)
      .inSingletonScope();

    // =====================================
    // COMMAND HANDLERS (Application Layer)
    // =====================================
    
    // Domain Handlers
    bind<CreateDomainHandler>(DDD_SYMBOLS.CreateDomainHandler)
      .to(CreateDomainHandler)
      .inSingletonScope();

    bind<UpdateDomainHandler>(DDD_SYMBOLS.UpdateDomainHandler)
      .to(UpdateDomainHandler)
      .inSingletonScope();

    bind<GetDomainHandler>(DDD_SYMBOLS.GetDomainHandler)
      .toDynamicValue((context) => {
        const queryService = context.container.get<DomainCQRSQueryService>(DDD_SYMBOLS.DomainCQRSQueryService);
        return new GetDomainHandler(queryService);
      })
      .inSingletonScope();

    bind<ListDomainsHandler>(DDD_SYMBOLS.ListDomainsHandler)
      .toDynamicValue((context) => {
        const queryService = context.container.get<DomainCQRSQueryService>(DDD_SYMBOLS.DomainCQRSQueryService);
        return new ListDomainsHandler(queryService);
      })
      .inSingletonScope();

    // Bounded Context Handlers
    bind<CreateBoundedContextHandler>(DDD_SYMBOLS.CreateBoundedContextHandler)
      .to(CreateBoundedContextHandler)
      .inSingletonScope();

    bind<GetBoundedContextHandler>(DDD_SYMBOLS.GetBoundedContextHandler)
      .to(GetBoundedContextHandler)
      .inSingletonScope();

    bind<ListBoundedContextsHandler>(DDD_SYMBOLS.ListBoundedContextsHandler)
      .toDynamicValue((context) => {
        const queryService = context.container.get<BoundedContextQueryService>(DDD_SYMBOLS.BoundedContextQueryService);
        return new ListBoundedContextsHandler(queryService);
      })
      .inSingletonScope();

    // Aggregate Handlers
    bind<CreateAggregateHandler>(DDD_SYMBOLS.CreateAggregateHandler)
      .to(CreateAggregateHandler)
      .inSingletonScope();

    bind<GetAggregateHandler>(DDD_SYMBOLS.GetAggregateHandler)
      .to(GetAggregateHandler)
      .inSingletonScope();

    // Project Handlers
    bind<CreateProjectHandler>(DDD_SYMBOLS.CreateProjectHandler)
      .to(CreateProjectHandler)
      .inSingletonScope();

    bind<GetProjectHandler>(DDD_SYMBOLS.GetProjectHandler)
      .to(GetProjectHandler)
      .inSingletonScope();

    bind<ListProjectsHandler>(DDD_SYMBOLS.ListProjectsHandler)
      .to(ListProjectsHandler)
      .inSingletonScope();

    // =====================================
    // APPLICATION SERVICES (Public API)
    // =====================================
    
    bind<DomainApplicationService>(DDD_SYMBOLS.DomainApplicationService)
      .to(DomainApplicationService)
      .inSingletonScope();

    bind<BoundedContextApplicationService>(DDD_SYMBOLS.BoundedContextApplicationService)
      .to(BoundedContextApplicationService)
      .inSingletonScope();

    bind<AggregateApplicationService>(DDD_SYMBOLS.AggregateApplicationService)
      .to(AggregateApplicationService)
      .inSingletonScope();

    bind<ProjectApplicationService>(DDD_SYMBOLS.ProjectApplicationService)
      .to(ProjectApplicationService)
      .inSingletonScope();

    bind<PackageMappingApplicationService>(DDD_SYMBOLS.PackageMappingApplicationService)
      .to(PackageMappingApplicationService)
      .inSingletonScope();

    // =====================================
    // AI TOOLS & INTEGRATION PORTS
    // =====================================
    
    // PersonLookupPort - Used by AI tools for person lookups
    bind<import('../../../../bounded-contexts/ai-tools/ports/PersonLookupPort').PersonLookupPort>(DDD_SYMBOLS.PersonLookupPort)
      .toDynamicValue(() => {
        const { PersonLookupAdapter } = require('../../../../bounded-contexts/ai-tools/infrastructure/adapters/PersonLookupAdapter');
        return new PersonLookupAdapter({
          enableCaching: true,
          cacheTTL: 300 // 5 minutes
        });
      })
      .inSingletonScope();

    // =====================================
    // META-MODEL DESIGN SERVICES
    // =====================================
    
    // Note: The meta-model design module should be loaded separately
    // for proper separation of concerns. This is a placeholder for future integration.
    // To use meta-model services, load both modules:
    // container.load(createDDDModule());
    // container.load(createMetaModelDesignModule());

    // =====================================
    // DOMAIN EXPERTS BOUNDED CONTEXT
    // =====================================
    // TODO: Fix Domain Experts bounded context integration
    // The domain experts configuration needs to be refactored to work with
    // the new module pattern. Currently disabled to avoid runtime errors.
  });
};